/* eslint-disable */


/// <reference path="BaseField.ts" />

namespace MACRAIL.Forms.Fields {

/**
 * Autocomplete textbox.
 *
 * Add a dropdown with suggestions based on what the user types.
 *
 * A few notes:
 *
 * - Generally, items should be an object with an 'id' and 'value' property. 'id' will be what the form returns through
 *   getValue() (i.e. to save to the database) while 'value' is the human readable text. You can change these properties
 *   if needed, but jQueryUI will always use the 'value' to display in the text box when an item is selected.
 *
 * - Item can contain an 'extra' property, which will be display in small text, right aligned.
 *
 * - If 'otherId' is given, the item with that ID will always be shown in the drop down, allowing the user to select a
 *   default item if there are no matches.
 *
 * - For display in the drop down, items will be sorted in the following order (assume user enters 'foo' as the search term):
 *   1. Exact match - 'Foo'.
 *   2. Items that start with the term, alphabetically - 'Foo bar', 'Foo baz'.
 *   3. Items that contain the term, alphabetically - 'Bar Foo', 'Baz Foo'.
 *   4. Other item, if given.
 *
 *   This order prioritises items closer to what the user is probably intending. For example when searching 'Track',
 *   'Trackman' is listed higher then 'Off-Track Yard Duties'.
 */
export class AutoComplete extends BaseField {
    protected opts: AutoCompleteOptions;

    private valueForHumans: string = '';
    private valueForHumansSelected: string = '';
    private settingValue: boolean = false;

    constructor (element: string | JQuery, optsPartial: Partial<AutoCompleteOptions>) {
        super(element, optsPartial);

        const defaultOpts: AutoCompleteOptions = {
            highlightTerm: true,
            mappingSource: [],
            mappingValue: 'id',
            mappingValueForHumans: 'value',
            maxItems: 30,
            minLength: 2,
            source: undefined,
            _renderItem: this.renderItem,
        };

        let opts: AutoCompleteOptions = Object.assign({}, defaultOpts, optsPartial);

        this.opts = opts;

        // Used with Form.autoConfigure.
        const pageVarName = 'autocomplete_' + this.element.attr('name') + '_' + this.element.data('rand-id');

        if (!opts.source || opts.source.length == 0) {
            if (PAGEVARS[pageVarName + '_items']) {
                opts.mappingSource = PAGEVARS[pageVarName + '_items'];
            }
        }

        if (!opts.otherId) {
            opts.otherId = this.element.data('other-id');
        }

        let options: JQueryUI.AutocompleteOptions = {
            'source': opts.source || this.getItems.bind(this),
            'select': (event: Event, ui: JQueryUI.AutocompleteUIParams) => {
                this.value = ui.item[this.opts.mappingValue];

                let valueForHumans = ui.item[this.opts.mappingValueForHumans];
                this.valueForHumans = valueForHumans;
                this.valueForHumansSelected = valueForHumans;

                this.element.blur();
                this.element.trigger('change');

                if (this.opts.onSelect) {
                    this.opts.onSelect(event, ui);
                }
            },
        };

        if (opts.delay) {
            options.delay = opts.delay;
        }

        if (opts.hasOwnProperty('minLength')) {
            options.minLength = opts.minLength;
        }

        this.element.autocomplete(options);

        if (this.element.data('value')) {
            this.setValue(this.element.data('value'));
        }

        if (opts.openOnFocus || opts.selectTextOnFocus) {
            this.element.on('focus', (e) => {
                if (this.opts.openOnFocus) {
                    this.element.autocomplete('search', '');
                }
                if (this.opts.selectTextOnFocus) {
                    (this.element[0] as HTMLInputElement).select();
                }
            });
        }

        if (opts._renderItem) {
            this.element.data('ui-autocomplete')._renderItem = opts._renderItem;
        }

        this.element.on('blur', (e) => {
            if (this.element.val() !== this.valueForHumansSelected) {
                this.element.val(this.valueForHumansSelected);
            }
        });
    }

    private getItems(request, response): void {
        let items = [];
        let otherItem = undefined;
        let otherItemShown = false;
        let term = request.term.toLowerCase();


        if (term.length < this.opts.minLength) {
            if (this.opts.mappingSource.length <= this.opts.maxItems) {
                for (let i = 0; i < this.opts.mappingSource.length; i++) {
                    items.push(this.opts.mappingSource[i]);
                }
            }
        } else {
            for (let i = 0; i < this.opts.mappingSource.length; i++) {
                let item = this.opts.mappingSource[i];

                if (item[this.opts.mappingValueForHumans].toLowerCase().indexOf(term) > -1) {
                    items.push(item);
                    if (item[this.opts.mappingValue] === this.opts.otherId) {
                        otherItemShown = true;
                    }
                }

                if (item[this.opts.mappingValue] == this.opts.otherId) {
                    otherItem = item;
                }
            }
        }

        items.sort((a, b) => {
            let labelA = a[this.opts.mappingValueForHumans].toLowerCase();
            let labelB = b[this.opts.mappingValueForHumans].toLowerCase();

            // Exact match
            if (labelA === term) return -1;
            if (labelB === term) return 1;

            // Match at start of value
            let indexOfA = labelA.indexOf(term);
            let indexOfB = labelB.indexOf(term);
            if (indexOfA === 0 && indexOfB !== 0) return -1;
            if (indexOfB === 0 && indexOfA !== 0) return 1;

            // Alphabetical
            if (labelA < labelB) return -1;
            if (labelA > labelB) return 1;

            // Exactly the same.
            return 0;
        });

        while (items.length > this.opts.maxItems) {
            items.pop();
        }

        if (otherItem && !otherItemShown) {
            if (items.length == this.opts.maxItems) {
                items.pop();
            }
            items.push(otherItem);
        }

        response(items);
    }

    /**
     * Render an item for the Activity and Sponsor fields.
     *
     * @param {HTMLUListElement} ul UL to render in.
     * @param {any} item Item to render.
     *
     * @return {HTMLLIElement} Menu item
     */
    private renderItem(ul: HTMLUListElement, item: any) {
        let li = $('<li>');
        let div = $('<div>');

        li.append(div);

        // @ts-ignore
        let termRegex = new RegExp($.ui.autocomplete.escapeRegex(this.term), 'i');

        div.append(item.value.replace(termRegex, '<span class="hilite">$&</span>'));

        if (item.extra && item.extra != '') {
            div.append('<span class="extra">' + item.extra + '</span>');
        }

        return li.appendTo(ul);
    }

    public setValue(newValue: any) {
        if (newValue === undefined) {
            this.value = undefined;
            this.setValueForHumans('');
            this.setDirty(true);
            this.element.trigger('change');
            return;
        }

        if (this.opts.mappingSource) {
            let mappingSource = this.opts.mappingSource;
            let mappingValue = this.opts.mappingValue;
            for (let item of mappingSource) {
                if (item[mappingValue] === newValue) {
                    this.value = newValue;
                    this.setValueForHumans(item[this.opts.mappingValueForHumans]);
                    this.setDirty(true);
                    this.element.trigger('change');

                    if (this.opts.onSelect && !this.settingValue) {
                        this.settingValue = true;
                        try {
                            this.opts.onSelect(new Event('change'), { item } as JQueryUI.AutocompleteUIParams);
                        } finally {
                            this.settingValue = false;
                        }
                    }

                    return;
                }
            }
        } else {
            super.setValue(newValue);
        }
    }

    public getValueForHumans() {
        return this.valueForHumans;
    }

    public setValueForHumans(valueForHumans: string) {
        this.valueForHumans = valueForHumans;
        this.valueForHumansSelected = valueForHumans;
        this.element.val(this.valueForHumans);
    }

    public setValues(value: any, valueForHumans: string) {
        this.value = value;
        this.setDirty(true);
        this.element.trigger('change');
        this.setValueForHumans(valueForHumans);
    }

}

interface AutoCompleteOptions extends FieldOptions {
    delay?: number;
    highlightTerm: boolean;
    mappingSource: any[];
    mappingValue: string;
    mappingValueForHumans: string;
    maxItems: number;
    minLength: number;
    onSelect?: (event: Event, ui: JQueryUI.AutocompleteUIParams) => void;
    openOnFocus?: boolean;
    otherId?: any;
    selectTextOnFocus?: boolean;
    source?: any;
    _renderItem?: (ul: HTMLUListElement, item: any) => void;
    _renderMenu?: (uli: JQuery, item: any[]) => void;
}

}
